import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import { MILEAGE_LIMIT_FIELD, MILEAGE_LIMIT_GROUP } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ADDITIONAL_CHARGE_TYPE, GROUP_CAPACITY_TYPE, OTHER_SETTING_TYPE } from '@/types/enums';
import { OtherSettingItem } from '@/types/system-setting';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default observer(function MileageLimitSetting() {
    // hook
    const { t } = useTranslation();

    // store
    const { systemSettingStore: { mileageLimitCMS, getMileageLimitAndDropOffSetting, updateMileageLimitAndDropOffSetting }, modalStore: { showAlertModal } } = useStore();

    // validate Schema
    const digitOnly = () =>
        yup
            .string()
            .trim()
            .checkInputOnlyPositiveDigitsOrEmpty(t('form_error_validate.only_number'));

    const makePriceDetailSchema = (isRequiredFields: boolean) => {
        const field = (required = false) =>
            required ?
                digitOnly().required(t('form_error_validate.required')) :
                digitOnly().optional().nullable();

        return yup.object({
            over24hKm: field(isRequiredFields),
            over48hKm: field(isRequiredFields),
            over72hKm: field(isRequiredFields),
            over96hKm: field(isRequiredFields),
            price: field(true) // price luôn required
        });
    };

    const capacityTypes = ['capacity125cc', 'capacity126cc'];
    const fieldArray = ['over24hKm', 'over48hKm', 'over72hKm', 'over96hKm', 'price'];

    const makeCapacitySchema = (priceSchema: yup.ObjectSchema<any>) =>
        yup
            .object(
                Object.fromEntries(
                    capacityTypes.map(type => [type, priceSchema.required()])
                )
            )
            .required('form_error_validate.required');

    const validateSchema = yup.object({
        variable: makeCapacitySchema(makePriceDetailSchema(true)), // overXXhKm required
        fixed: makeCapacitySchema(makePriceDetailSchema(false)) // overXXhKm optional
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            variable: {
                capacity125cc: {
                    over24hKm: '0',
                    over48hKm: '0',
                    over72hKm: '0',
                    over96hKm: '0',
                    price: '0'
                },
                capacity126cc: {
                    over24hKm: '0',
                    over48hKm: '0',
                    over72hKm: '0',
                    over96hKm: '0',
                    price: '0'
                }
            },
            fixed: {
                capacity125cc: {
                    over24hKm: null,
                    over48hKm: null,
                    over72hKm: null,
                    over96hKm: null,
                    price: '0'
                },
                capacity126cc: {
                    over24hKm: null,
                    over48hKm: null,
                    over72hKm: null,
                    over96hKm: null,
                    price: '0'
                }
            }
        }
    });

    // life cycle

    useEffect(() => {
        if (mileageLimitCMS && Object.keys(mileageLimitCMS).length > 0) {
            let variable = mileageLimitCMS.variable;
            let fixed = mileageLimitCMS.fixed;
            let var_capacity125cc = variable.capacity125cc?.length > 0 ? variable.capacity125cc[0] : {} as OtherSettingItem;
            let var_capacity126cc = variable.capacity126cc?.length > 0 ? variable.capacity126cc[0] : {} as OtherSettingItem;
            let fixed_capacity125cc = fixed.capacity125cc?.length > 0 ? fixed.capacity125cc[0] : {} as OtherSettingItem;
            let fixed_capacity126cc = fixed.capacity126cc?.length > 0 ? fixed.capacity126cc[0] : {} as OtherSettingItem;

            form.reset({
                variable: {
                    capacity125cc: {
                        over24hKm: var_capacity125cc.over24hKm ?? '0',
                        over48hKm: var_capacity125cc.over48hKm ?? '0',
                        over72hKm: var_capacity125cc.over72hKm ?? '0',
                        over96hKm: var_capacity125cc.over96hKm ?? '0',
                        price: var_capacity125cc.price ?? '0'
                    },
                    capacity126cc: {
                        over24hKm: var_capacity126cc.over24hKm ?? '0',
                        over48hKm: var_capacity126cc.over48hKm ?? '0',
                        over72hKm: var_capacity126cc.over72hKm ?? '0',
                        over96hKm: var_capacity126cc.over96hKm ?? '0',
                        price: var_capacity126cc.price ?? '0'
                    }
                },
                fixed: {
                    capacity125cc: {
                        over24hKm: null,
                        over48hKm: null,
                        over72hKm: null,
                        over96hKm: null,
                        price: fixed_capacity125cc.price ?? '0'
                    },
                    capacity126cc: {
                        over24hKm: null,
                        over48hKm: null,
                        over72hKm: null,
                        over96hKm: null,
                        price: fixed_capacity126cc.price ?? '0'
                    }
                }
            });
        }
    }, [mileageLimitCMS]);

    // function
    const onSubmit = async (data: FormData) => {
        const payload = {
            format: OTHER_SETTING_TYPE.MILEAGE_LIMIT,
            data: {
                variable: {
                    capacity125cc: {
                        ...data.variable.capacity125cc,
                        additionalChargesType: ADDITIONAL_CHARGE_TYPE.VARIABLE,
                        format: OTHER_SETTING_TYPE.MILEAGE_LIMIT,
                        capacityType: GROUP_CAPACITY_TYPE.LQ_125CC
                    },
                    capacity126cc: {
                        ...data.variable.capacity126cc,
                        additionalChargesType: ADDITIONAL_CHARGE_TYPE.VARIABLE,
                        format: OTHER_SETTING_TYPE.MILEAGE_LIMIT,
                        capacityType: GROUP_CAPACITY_TYPE.GQ_126CC
                    }
                },
                fixed: {
                    capacity125cc: {
                        ...data.fixed.capacity125cc,
                        additionalChargesType: ADDITIONAL_CHARGE_TYPE.FIXED,
                        format: OTHER_SETTING_TYPE.MILEAGE_LIMIT,
                        capacityType: GROUP_CAPACITY_TYPE.LQ_125CC
                    },
                    capacity126cc: {
                        ...data.fixed.capacity126cc,
                        additionalChargesType: ADDITIONAL_CHARGE_TYPE.FIXED,
                        format: OTHER_SETTING_TYPE.MILEAGE_LIMIT,
                        capacityType: GROUP_CAPACITY_TYPE.GQ_126CC
                    }
                }
            }
        };
        const res = await flowResult(updateMileageLimitAndDropOffSetting(payload));
        if (res) {
            getMileageLimitAndDropOffSetting();
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('messages.update_success')
            });
        }
    };

    const scrollRef = useRef<HTMLDivElement>(null);
    const onFirstScroll = useRef<boolean>(true);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (el.scrollLeft > 0 && onFirstScroll.current) {
                onFirstScroll.current = false;
            }
        };
        el.addEventListener('scroll', handleScroll);
        return () => {
            el.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div className='layout-card'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-6'>
                    <div>
                        <h3><FormLabel required>{t('field.content')}</FormLabel></h3>
                        <div className='overflow-x-auto max-w-full' ref={scrollRef}>
                            <div className='flex max-w-full'>
                                <div className='min-w-[220px] min-h-[60px] p-[15px] border-x border-b border-border-gray2 bg-subtle'></div>
                                {fieldArray.map((field, indx) => {
                                    return (
                                        <div key={indx} className='min-w-[190px] min-h-[60px] leading-[20px] bg-subtle p-[15px] font-medium flex flex-col items-center justify-center text-center border-r border-b border-border-gray2'>
                                            <p>{MILEAGE_LIMIT_FIELD[field].label}</p>
                                            {
                                                MILEAGE_LIMIT_FIELD.price.field !== field && <p>基準走行距離</p>
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                            {
                                capacityTypes.map((capacityType) => {
                                    return (
                                        <div key={capacityType} className='flex'>
                                            <div className='min-w-[220px] min-h-[60px] bg-subtle font-medium flex items-center justify-end p-[15px] border-b border-x border-border-gray2 text-right'>{MILEAGE_LIMIT_GROUP[capacityType].label}</div>
                                            {fieldArray.map((fieldName, indx) => {
                                                return (
                                                    <div key={indx} className='min-w-[190px] w-[190px] min-h-[60px] p-[15px] border-r border-b border-border-gray2'>
                                                        <FormField
                                                            control={form.control}
                                                            name={`variable.${capacityType}.${fieldName}`}
                                                            render={({ field }) => {
                                                                return (
                                                                    <FormItem>
                                                                        <FormControl>
                                                                            <div className='flex items-center'>
                                                                                <FormatNumberVer2
                                                                                    decimalScale={0}
                                                                                    suffix={fieldName !== MILEAGE_LIMIT_FIELD.price.field ? 'km' : ''}
                                                                                    statusDisplay='input'
                                                                                    thousandSeparator={true}
                                                                                    allowNegative={false}
                                                                                    placeholder={fieldName === 'price' ? '' : 'km'}
                                                                                    className='h-[30px] border rounded py-1 px-2 font-normal disabled:cursor-default'
                                                                                    value={field.value}
                                                                                    setvalueOfNumericFormat={(values) => {
                                                                                        field.onChange(values);
                                                                                    }}
                                                                                />
                                                                                {
                                                                                    fieldName === 'price' && (
                                                                                        <div>
                                                                                            円/
                                                                                            {MILEAGE_LIMIT_GROUP[capacityType].km}
                                                                                            km
                                                                                        </div>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        </FormControl>
                                                                        <FormMessage />
                                                                    </FormItem>
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                    <div>
                        <h3><FormLabel required>{t('words_title.discounted_fee')}</FormLabel></h3>
                        {
                            capacityTypes.map((capacityType, index) => {
                                return (
                                    <div key={capacityType} className='flex'>
                                        <div className={classNames(
                                            'min-w-[220px] min-h-[60px] flex items-center font-medium bg-subtle justify-end p-[15px] border-b border-x border-border-gray2',
                                            index === 0 && 'border-t'
                                        )}
                                        >
                                            {MILEAGE_LIMIT_GROUP[capacityType].label}
                                        </div>
                                        <div className={classNames(
                                            'min-w-[190px] w-[190px] min-h-[60px] p-[15px] border-r border-b border-border-gray2',
                                            index === 0 && 'border-t'
                                        )}
                                        >
                                            <FormField
                                                control={form.control}
                                                name={`fixed.${capacityType}.price`}
                                                render={({ field }) => {
                                                    return (
                                                        <FormItem>
                                                            <FormControl>
                                                                <div className='flex items-center'>
                                                                    <FormatNumberVer2
                                                                        decimalScale={0}
                                                                        statusDisplay='input'
                                                                        thousandSeparator={true}
                                                                        allowNegative={false}
                                                                        className='h-[30px] border rounded py-1 px-2 font-normal disabled:cursor-default'
                                                                        value={field.value}
                                                                        setvalueOfNumericFormat={(values) => {
                                                                            field.onChange(values);
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        円/
                                                                        {MILEAGE_LIMIT_GROUP[capacityType].km}
                                                                        km
                                                                    </div>
                                                                </div>
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                    <div className='flex justify-center mb-2'>
                        <ButtonCus variant='default' type='submit' size='md'>{t('button.save')}</ButtonCus>
                    </div>
                </form>

            </Form>

        </div>
    );
});
